import React, { useEffect, useState } from 'react';
import { Download, Calendar, LogOut, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserSketches, deleteSketch } from '../services/supabaseService';
import { Sketch } from '../types';

const HistoryGallery: React.FC = () => {
  const { user, signOut } = useAuth();
  const [sketches, setSketches] = useState<Sketch[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const loadSketches = async () => {
      if (user) {
        const data = await getUserSketches();
        setSketches(data);
        setLoading(false);
      }
    };
    loadSketches();
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDelete = async (id: string, imageUrl: string) => {
    if (window.confirm('Are you sure you want to delete this sketch? This cannot be undone.')) {
      setDeletingId(id);
      const success = await deleteSketch(id, imageUrl);
      if (success) {
        setSketches(prev => prev.filter(s => s.id !== id));
      }
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 animate-in fade-in duration-500">
      <div className="flex justify-between items-end mb-8 border-b-2 border-gray-200 pb-4">
        <div>
            <h2 className="text-3xl font-bold font-hand">
            My <span className="highlight px-2">Sketch Vault</span>
            </h2>
            <p className="text-gray-500 font-hand mt-2">
                {sketches.length} {sketches.length === 1 ? 'sketch' : 'sketches'} saved
            </p>
        </div>
        <button 
            onClick={signOut}
            className="text-xs text-gray-400 hover:text-red-500 font-hand flex items-center gap-1 mb-1 transition-colors"
        >
            <LogOut size={14} /> Sign Out
        </button>
      </div>

      {sketches.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="font-hand text-xl text-gray-400">No sketches found yet.</p>
          <p className="font-hand text-gray-400 mt-2">Go create something amazing!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {sketches.map((sketch) => (
            <div key={sketch.id} className="group bg-white p-3 pb-2 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,0.2)] transition-all hover:-translate-y-1 relative">
              <div className="aspect-square bg-gray-100 border border-gray-200 overflow-hidden mb-3 relative">
                 <img 
                    src={sketch.image_url} 
                    alt={sketch.concept} 
                    className="w-full h-full object-cover filter contrast-110"
                    loading="lazy"
                 />
                 {/* Hover Overlay */}
                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button 
                        onClick={() => handleDownload(sketch.image_url)}
                        className="bg-white p-2 rounded-full border-2 border-black shadow-md hover:scale-110 transition-transform"
                        title="Download Original"
                    >
                        <Download size={20} />
                    </button>
                 </div>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between items-start">
                    <p className="font-hand text-sm font-bold leading-tight line-clamp-2 h-10 text-gray-800 flex-1 mr-2">
                        {sketch.concept}
                    </p>
                    <button 
                        onClick={() => handleDelete(sketch.id, sketch.image_url)}
                        className="text-gray-300 hover:text-red-500 transition-colors p-1"
                        title="Delete Sketch"
                        disabled={deletingId === sketch.id}
                    >
                        {deletingId === sketch.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-200">
                    <div className="flex items-center gap-1 text-xs text-gray-400 font-hand">
                        <Calendar size={12} />
                        <span>{formatDate(sketch.created_at)}</span>
                    </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryGallery;